/**
 * Dispatch filters state and derivations: project/scope/worker multi-select
 * filters plus empty-project visibility, with the same cross-curation pattern
 * as the Schedule filters (options narrow each other, selections are never
 * hidden or silently dropped).
 */
import { useCallback, useMemo, useState } from "react";

import { toLocalDateKey } from "../../../utils/datetime";
import {
  getEffectiveFilterIds,
  mergeSelectedFilterOptions,
} from "../shared/filters/planningFilterModel";
import {
  collectDispatchProjectOptions,
  collectDispatchScopeOptions,
  collectDispatchWorkerOptions,
  dispatchRowMatchesProjectFilter,
  dispatchRowMatchesScopeFilter,
  dispatchRowMatchesWorkerFilter,
  filterDispatchScopeRows,
} from "./dispatchFilterModel";

/**
 * Filter state + derived option lists and filtered jobs/scopes for Dispatch.
 *
 * Curated option lists are narrowed by the *other* filters' raw selections;
 * already-selected options are merged back in so chips stay visible. The
 * effective ids applied to the page are validated against the base
 * (uncurated) lists, so selections only stop applying when the item leaves
 * the visible week entirely.
 *
 * @param {Object} params
 * @param {Array<Object>|null} params.jobs Weekly dispatch jobs.
 * @param {Array<Object>|null} params.scopes Weekly WorkScopeAspect rows.
 * @param {Array<Date>} params.datesOfWeek Visible days (already respects Show Weekends).
 * @param {Object|null} [params.singleProject=null] Optional project used to lock project scoping.
 * @returns {Object} Filter state, setters, options, and `filteredJobs`/`filteredScopes`.
 */
export default function useDispatchFilters({
  jobs,
  scopes,
  datesOfWeek,
  singleProject = null,
}) {
  const singleProjectId =
    singleProject?.id == null ? null : String(singleProject.id);
  // Show projects with no scopes in the visible week by default.
  const [hideEmptyProjects, setHideEmptyProjects] = useState(false);
  const [filterProjectId, setFilterProjectId] = useState(() =>
    singleProjectId ? [singleProjectId] : []
  );
  const [filterScopeId, setFilterScopeId] = useState([]);
  const [filterWorkerId, setFilterWorkerId] = useState([]);

  // Keep single-project embeds locked to the provided project id.
  const effectiveFilterProjectSelection = useMemo(
    () => (singleProjectId ? [singleProjectId] : filterProjectId),
    [singleProjectId, filterProjectId]
  );

  /** Rows on currently visible days (weekend rows drop out when weekends are hidden). */
  const visibleScopeRows = useMemo(() => {
    const visibleDayKeys = new Set((datesOfWeek ?? []).map(toLocalDateKey));
    return (scopes ?? []).filter((row) => visibleDayKeys.has(String(row.date)));
  }, [scopes, datesOfWeek]);

  // Base (uncurated) option lists: validate selections and keep selected chips visible.
  const dispatchFilterProjectOptionsBase = useMemo(
    () => collectDispatchProjectOptions(jobs),
    [jobs]
  );
  const dispatchFilterScopeOptionsBase = useMemo(
    () => collectDispatchScopeOptions(visibleScopeRows),
    [visibleScopeRows]
  );
  const dispatchFilterWorkerOptionsBase = useMemo(
    () => collectDispatchWorkerOptions(visibleScopeRows),
    [visibleScopeRows]
  );

  /** Rows limited to the selected projects (raw selection); base for the scope/worker cascades. */
  const visibleRowsInSelectedProjects = useMemo(
    () =>
      visibleScopeRows.filter((row) =>
        dispatchRowMatchesProjectFilter(row, effectiveFilterProjectSelection)
      ),
    [visibleScopeRows, effectiveFilterProjectSelection]
  );

  // Available scope options reflect the selected projects and workers;
  // already-selected scopes are merged back in.
  const dispatchFilterScopeOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectDispatchScopeOptions(
          visibleRowsInSelectedProjects.filter((row) =>
            dispatchRowMatchesWorkerFilter(row, filterWorkerId)
          )
        ),
        dispatchFilterScopeOptionsBase,
        filterScopeId
      ),
    [
      visibleRowsInSelectedProjects,
      filterWorkerId,
      dispatchFilterScopeOptionsBase,
      filterScopeId,
    ]
  );

  // Available worker options reflect the selected projects and scopes;
  // already-selected workers are merged back in.
  const dispatchFilterWorkerOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectDispatchWorkerOptions(
          visibleRowsInSelectedProjects.filter((row) =>
            dispatchRowMatchesScopeFilter(row, filterScopeId)
          )
        ),
        dispatchFilterWorkerOptionsBase,
        filterWorkerId
      ),
    [
      visibleRowsInSelectedProjects,
      filterScopeId,
      dispatchFilterWorkerOptionsBase,
      filterWorkerId,
    ]
  );

  // Available project options reflect the selected scopes/workers and the
  // empty-project rule; already-selected projects are merged back in.
  const dispatchFilterProjectOptions = useMemo(() => {
    const scopeWorkerFilterActive =
      filterScopeId.length > 0 || filterWorkerId.length > 0;
    const jobIdsWithMatchingRows = new Set(
      visibleScopeRows
        .filter(
          (row) =>
            dispatchRowMatchesScopeFilter(row, filterScopeId) &&
            dispatchRowMatchesWorkerFilter(row, filterWorkerId)
        )
        .map((row) => String(row.job?.id))
    );
    const offeredJobs = (jobs ?? []).filter((job) => {
      if (scopeWorkerFilterActive || hideEmptyProjects) {
        return jobIdsWithMatchingRows.has(String(job.id));
      }
      return true;
    });
    return mergeSelectedFilterOptions(
      collectDispatchProjectOptions(offeredJobs),
      dispatchFilterProjectOptionsBase,
      filterProjectId
    );
  }, [
    jobs,
    visibleScopeRows,
    filterScopeId,
    filterWorkerId,
    hideEmptyProjects,
    dispatchFilterProjectOptionsBase,
    filterProjectId,
  ]);

  // Effective ids are validated against the base lists (everything in the
  // visible week), not the curated lists, so cross-curation never silently
  // drops an active filter. Single-project embeds stay locked to that project.
  const effectiveFilterProjectId = useMemo(
    () =>
      singleProjectId
        ? [singleProjectId]
        : getEffectiveFilterIds(filterProjectId, dispatchFilterProjectOptionsBase),
    [singleProjectId, filterProjectId, dispatchFilterProjectOptionsBase]
  );
  const effectiveFilterScopeId = useMemo(
    () => getEffectiveFilterIds(filterScopeId, dispatchFilterScopeOptionsBase),
    [filterScopeId, dispatchFilterScopeOptionsBase]
  );
  const effectiveFilterWorkerId = useMemo(
    () => getEffectiveFilterIds(filterWorkerId, dispatchFilterWorkerOptionsBase),
    [filterWorkerId, dispatchFilterWorkerOptionsBase]
  );

  /** Rows matching all active filters; feeds the visible scope cards. */
  const filteredScopes = useMemo(
    () =>
      filterDispatchScopeRows(visibleScopeRows, {
        projectIds: effectiveFilterProjectId,
        scopeIds: effectiveFilterScopeId,
        workerIds: effectiveFilterWorkerId,
      }),
    [
      visibleScopeRows,
      effectiveFilterProjectId,
      effectiveFilterScopeId,
      effectiveFilterWorkerId,
    ]
  );

  /**
   * Jobs to render as rows: project filter applied, and jobs without any
   * remaining scope rows hidden when hide-empty is on or any filter is active.
   * Single-project embeds keep the project row visible even when empty for the week.
   */
  const filteredJobs = useMemo(() => {
    const scopeWorkerFilterActive =
      effectiveFilterScopeId.length > 0 || effectiveFilterWorkerId.length > 0;
    const jobIdsWithRows = new Set(
      filteredScopes.map((row) => String(row.job?.id))
    );
    const scopedJobs = (jobs ?? []).filter((job) => {
      if (
        effectiveFilterProjectId.length > 0 &&
        !effectiveFilterProjectId.includes(String(job.id))
      ) {
        return false;
      }
      if (scopeWorkerFilterActive || hideEmptyProjects) {
        return jobIdsWithRows.has(String(job.id));
      }
      return true;
    });
    if (
      scopedJobs.length ||
      !singleProjectId ||
      !singleProject ||
      scopeWorkerFilterActive
    ) {
      return scopedJobs;
    }
    // Keep the locked project visible in compact single-project embeds.
    return [singleProject];
  }, [
    jobs,
    filteredScopes,
    effectiveFilterProjectId,
    effectiveFilterScopeId,
    effectiveFilterWorkerId,
    hideEmptyProjects,
    singleProjectId,
    singleProject,
  ]);

  /** Whether any panel-owned filter differs from its default (excludes Show Weekends). */
  const dispatchFilterActive =
    hideEmptyProjects ||
    (!singleProjectId && filterProjectId.length > 0) ||
    filterScopeId.length > 0 ||
    filterWorkerId.length > 0;

  /** Reset all panel-owned filters to defaults (Show Weekends is owned by WeeklyContext). */
  const clearDispatchFilters = useCallback(() => {
    setHideEmptyProjects(false);
    if (!singleProjectId) {
      setFilterProjectId([]);
    }
    setFilterScopeId([]);
    setFilterWorkerId([]);
  }, [singleProjectId]);

  return useMemo(
    () => ({
      hideEmptyProjects,
      setHideEmptyProjects,
      filterProjectId: effectiveFilterProjectSelection,
      setFilterProjectId,
      filterScopeId,
      setFilterScopeId,
      filterWorkerId,
      setFilterWorkerId,
      dispatchFilterProjectOptions,
      dispatchFilterScopeOptions,
      dispatchFilterWorkerOptions,
      filteredJobs,
      filteredScopes,
      dispatchFilterActive,
      clearDispatchFilters,
    }),
    [
      hideEmptyProjects,
      effectiveFilterProjectSelection,
      filterScopeId,
      filterWorkerId,
      dispatchFilterProjectOptions,
      dispatchFilterScopeOptions,
      dispatchFilterWorkerOptions,
      filteredJobs,
      filteredScopes,
      dispatchFilterActive,
      clearDispatchFilters,
    ]
  );
}
